import React from 'react';
import Page from 'components/page/Page';
import { Dropdown, LoadingSpinner, SelectOption, useBoundForm, useFetch } from 'common.ui';
import { profileClient } from 'api/MinSideClients';
import { GenericValidationError, NameChangeRequest, ResponseError, UserType } from 'api/minside';
import Container from 'react-bootstrap/Container';
import { UserPropertyGroup } from 'components/profile/UserPropertyGroup';
import { useTranslation } from 'react-i18next';
import { SupportedLanguage } from 'localization/SupportedLanguage';
import { supportedLanguages } from 'localization/LanguageConfig';
import { useUser } from 'contexts/UserContext';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InternalUserInfo from './InternalUserInfo';
import ExternalUserInfo from './ExternalUserInfo';
import { meApiClient } from 'api/MinSideClients';
import { store } from 'react-notifications-component';
import { Button, SimpleModal } from 'common.ui';
import { useHistory, useLocation } from 'react-router-dom';
import RoutePaths from 'RoutePaths';
import { changeSigninEmailErrorParameter, emailAlreadyInUseError } from 'auth/ChangeSigninEmailState';

import './Profile.scss';

function UserInfo() {
  const { t } = useTranslation('pages');
  const { changePreferredLanguage, preferredLangauge } = useUser();
  const [user, , , refetchUser] = useFetch(async () => profileClient.apiProfileCurrentGet(), undefined, false, []);
  const [showPhoneDeleteModal, setShowPhoneDeleteModal] = React.useState(false);
  const [deletingPhone, setDeletingPhone] = React.useState(false);
  const [showNameChangeDialog, setShowNameChangeDialog] = React.useState(false);
  const [changingName, setChangingName] = React.useState(false);
  const [nameChangeValues, setNameChangeValues] = React.useState<NameChangeRequest>({});
  const [apiErrors, setApiErrors] = React.useState<GenericValidationError>();
  const { search } = useLocation();
  const { replace } = useHistory();
  const showEmailAlreadyInUseModal =
    new URLSearchParams(search).get(changeSigninEmailErrorParameter) === emailAlreadyInUseError;
  // Fjerner parameteren fra url-en, slik at dialogen ikke vises på nytt ved oppdatering av siden.
  const closeEmailAlreadyInUseModal = () => replace(RoutePaths.profile);

  const langs: SelectOption<SupportedLanguage>[] = supportedLanguages.map((x) => {
    return { id: x.id, text: x.displayName, value: x };
  });

  // Oppdaterer skjemaverdiene under render når brukeren er hentet på nytt, i stedet for i en effekt
  const [previousUser, setPreviousUser] = React.useState(user);
  if (previousUser !== user) {
    setPreviousUser(user);
    if (user) {
      setNameChangeValues({
        firstName: user.firstName,
        surName: user.surName
      });
    }
  }

  const { form: nameChangeForm, FormContainer: NameChangeFormContainer, Input, DisplayErrors } = useBoundForm<NameChangeRequest>({
    onSubmit: async (submitModel) => {
      setChangingName(true);
      setNameChangeValues(submitModel);
      try {
        await profileClient.apiProfileNamePut({
          nameChangeRequest: {
            firstName: submitModel.firstName,
            surName: submitModel.surName
          }
        });
        setApiErrors(undefined);
        store.addNotification({
          title: t('externalUserInfo.changeNameModal.successTitle'),
          message: t('externalUserInfo.changeNameModal.successMessage'),
          type: 'success',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 10000
          }
        });
        await refetchUser();
        setShowNameChangeDialog(false);
      } catch (e) {
        if (e instanceof ResponseError && e?.response.status === 400) {
          const result = (await e.response.json()) as GenericValidationError;
          if (result) {
            setApiErrors(result);
            return;
          }
        }

        store.addNotification({
          title: t('externalUserInfo.changeNameModal.errorTitle'),
          message: t('externalUserInfo.changeNameModal.errorMessage'),
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 10000
          }
        });
      } finally {
        setChangingName(false);
      }
    },
    model: nameChangeValues,
    errors: apiErrors
  });

  const onOpenNameChangeDialog = () => {
    if (!user) {
      return;
    }

    setNameChangeValues({
      firstName: user.firstName,
      surName: user.surName
    });
    setApiErrors(undefined);
    setShowNameChangeDialog(true);
  };

  if (!user) {
    return <LoadingSpinner />;
  }

  return (
    <Page header={t('userInfo.title')} title={t('userInfo.title')}>
      {user.userType === UserType.Internal && <InternalUserInfo user={user} />}
      {user.userType === UserType.External && <ExternalUserInfo user={user} onDeletePhone={() => setShowPhoneDeleteModal(true)} onNameChange={onOpenNameChangeDialog} />}

      <Container fluid>
        <SimpleModal
          header={t('externalUserInfo.changeSigninEmail.errorTitle')}
          show={showEmailAlreadyInUseModal}
          onCancel={closeEmailAlreadyInUseModal}
        >
          <p>{t('externalUserInfo.changeSigninEmail.errorMessage')}</p>
          <Row className='delete-modal-button-row'>
            <Button
              type='button'
              text={t('externalUserInfo.changeSigninEmail.btnClose')}
              onClick={closeEmailAlreadyInUseModal}
            />
          </Row>
        </SimpleModal>
        <SimpleModal
          header={t('externalUserInfo.deleteModal.title')}
          show={showPhoneDeleteModal}
          onCancel={() => {setShowPhoneDeleteModal(false)}}
        >
          <p>{t('externalUserInfo.deleteModal.text')}</p>
          <Row className='delete-modal-button-row'>
            <Button
              type='button'
              loading={deletingPhone}
              text={t('externalUserInfo.deleteModal.btnDelete')}
              onClick={() => {
                setDeletingPhone(true);
                meApiClient.apiMePhoneDelete()
                .then(async () => {
                  store.addNotification({
                    title: t('externalUserInfo.deleteModal.successTitle'),
                    message: t('externalUserInfo.deleteModal.successMessage'),
                    type: 'success',
                    insert: 'top',
                    container: 'top-right',
                    animationIn: ['animate__animated', 'animate__fadeIn'],
                    animationOut: ['animate__animated', 'animate__fadeOut'],
                    dismiss: {
                      duration: 10000
                    }
                  });
                  await refetchUser();
                }).catch(() => {
                  store.addNotification({
                    title: t('externalUserInfo.deleteModal.errorTitle'),
                    message: t('externalUserInfo.deleteModal.errorMessage'),
                    type: 'danger',
                    insert: 'top',
                    container: 'top-right',
                    animationIn: ['animate__animated', 'animate__fadeIn'],
                    animationOut: ['animate__animated', 'animate__fadeOut'],
                    dismiss: {
                      duration: 10000
                    }
                  });
                }).finally(() => {
                  setShowPhoneDeleteModal(false);
                  setDeletingPhone(false);
                });
              }}
            />
            <Button
              type='button'
              styleType='light'
              text={t('externalUserInfo.deleteModal.btnCancel')}
              onClick={() => {setShowPhoneDeleteModal(false)}}
            />
          </Row>
        </SimpleModal>
        <SimpleModal
          header={t('externalUserInfo.changeNameModal.title')}
          show={showNameChangeDialog}
          onCancel={() => {
            setApiErrors(undefined);
            setShowNameChangeDialog(false);
          }}
        >
          <NameChangeFormContainer form={nameChangeForm}>
            <p>{t('externalUserInfo.changeNameModal.text')}</p>
            <Row>
              <Col lg='8'>
                <DisplayErrors form={nameChangeForm} />
              </Col>
            </Row>
            <Row>
              <Col lg='8'>
                <Input
                  form={nameChangeForm}
                  name='firstName'
                  label={t('externalUserInfo.changeNameModal.firstNameLabel')}
                  placeholder={t('externalUserInfo.changeNameModal.firstNamePlaceholder')}
                />
                <Input
                  form={nameChangeForm}
                  name='surName'
                  label={t('externalUserInfo.changeNameModal.lastNameLabel')}
                  placeholder={t('externalUserInfo.changeNameModal.lastNamePlaceholder')}
                />
              </Col>
            </Row>
            <Row className='delete-modal-button-row'>
              <Button
                type='submit'
                loading={changingName}
                text={t('externalUserInfo.changeNameModal.btnChange')}
              />
              <Button
                type='button'
                styleType='light'
                text={t('externalUserInfo.changeNameModal.btnCancel')}
                onClick={() => {
                  setApiErrors(undefined);
                  setShowNameChangeDialog(false);
                }}
              />
            </Row>
          </NameChangeFormContainer>
        </SimpleModal>
        <Row>
          <Col lg='8'>
            <UserPropertyGroup label={t('userInfo.languageSelector.label')} />
            <Dropdown
              options={langs}
              id='langSelector'
              key={preferredLangauge}
              labelText={t('userInfo.languageSelector.labelText')}
              value={preferredLangauge}
              onChangeSelection={async (_k, v) => {
                const val = v as SupportedLanguage;
                await changePreferredLanguage(val.id);
              }}
            />
          </Col>
        </Row>
      </Container>
    </Page>
  );
}

export default UserInfo;
